<!-- =========================================================================================
    Base cho ô number input
    Bao gồm các tính năng:
      + Tự động fomat loại tiền tệ, số lượng (có ngăn cách giữa các số phần nguyên và phân tách phần nguyên và phần thập phân)
      + Số tự động căn phải
      + Auto foucus
    Created by nnanh2 - 24/06/2019
========================================================================================== -->

<template>
  <div class="flex-col">
    <div
      class="w-full "
      :style="styleInput"
    >
      <div
        v-show="editingMode"
        class="flex h-full items-center w-full"
      >
        <div
          class="ms-number ms-number-container flex items-center bg-white h-full"
          :class="{'isValid':errorProvider.isValid, 'input-readOnly': readOnly }"
        >
          <div
            class="ms-number-input"
            :class="[{ 'input-readOnly': readOnly }]"
          >
            <input
              :ref="controlRef"    
              v-bind="$attrs"
              :autofocus="autofocus"
              :step="step"
              :readonly="readOnly"
              :class="[{ 'input-readOnly': readOnly },{'pr-10':hasCellButtons}, 'w-full']"
              :tabindex="tabindex"
              :style="{height: height + 'px', width: width + 'px', textAlign: textAlign}"
              :disabled="disabled"
              v-on="listeners"
              @keydown="upDownValue"
              @click="onInputClick"
              @focus="onInputFocus"
            >
          </div>
          <!-- <ms-tooltip v-if="!disabled && errorProvider.isValid" :showTooltips="false">
            <div class="icon-input" v-if="!disabled && errorProvider.isValid">
              <div class="mi mi-20 mi-icon-error"></div>
            </div>
          </ms-tooltip> -->
          <div
            v-if="isSpin"
            class="ms-number-spin"
            style="overflow:hidden"
            :class="{ 'input-readOnly': readOnly }"
          >
            <!-- class="h-15 mi-chevron-up cursor-pointer" -->
            <div
              class="mi-15 mi-chevron-up-small w-full"
              @click="plus()"
            />
            <!-- class="h-15 mi-chevron-down cursor-pointer" -->
            <!-- style="mask-position:-100px -3px" -->
            <div
              class="mi-15 mi-chevron-down-small w-full"
              @click="less()"
            />
          </div>
        </div>
        <div
          v-if="editable"
          class="flex m-l-8"
        >
          <ms-icon
            icon="mi-file-green"
            :tooltip-content="$t('i18nCommon.Save')"
            @click="saveEditMode"
          />
          <ms-icon
            icon="mi-circle-close-red"
            :tooltip-content="$t('i18nCommon.Cancel2')"
            @click="closeEditMode"
          />
        </div>
      </div>
      <div
        v-show="editable && !editingMode"
        class="flex"
        @dblclick="onDoubleClick"
      >
        <ms-text-border class="text-border">
          <slot name="custom-text">
            <span
              v-show="internalText || autoZero"
              :title="getInternalText"
            >{{ getInternalText }}</span>
          </slot>
          <template #icon>
            <ms-icon
              v-if="permission"
              icon="mi-pencil"
              :tooltip-content="$t('i18nCommon.Edit')"
              @click="clickEditBtn"
            />
            <ms-icon
              v-if="!permission"
              icon="mi-lock"
            />
          </template>
        </ms-text-border>
      </div>
    </div>
    <p
      v-show="!disabled && errorProvider.isValid && ((editable && editingMode) || !editable)"
      :class="{'text-error': errorProvider.isValid}"
    >
      {{ errorProvider.message }}
    </p>
  </div>
</template>

<script >
import AutoNumeric from "autonumeric";
import msBaseComponent from "@/components/controls/msbase/msBaseComponent.vue";
import { formatMoney } from "./convertCurrency";
import { MsControlRef } from "@/models/enums/MsControlRefConst.js";

export default {
  name: "MsNumber",
  extends: msBaseComponent,
  inheritAttrs: false,

  props: {
    textAlign: {
      type: String,
      default: "right"
    },
    value: {
      default: "",
      type: [String, Number]
    },
    /**
     * Cấu hình cho icon bên phải
     */
    hasCellButtons: {
      default: false,
      type: Boolean
    },
    /**
     * Giá trị nhỏ nhất cho phép nhập
     */
    min: {
      default: 0,
      type: [Number, String]
    },
    /**
     * Giá trị lớn nhất cho phép nhập
     */
    max: {
      default: 100000000000000,
      type: [Number, String]
    },
    /**
     * Tự động focus
     */
    autofocus: {
      default: false,
      type: [Boolean, String]
    },
    /**
     * Bước nhảy của giá trị
     */
    step: {
      default: 1,
      type: [Number, String]
    },
    /**
     * Chiều cao của input
     */
    height: {
      //chiều cao của ô input
      default: 36,
      type: [Number, String]
    },
    /**
     * Chiều dài của input
     */
    width: {
      //chiều cao của ô input
      default: "auto",
      type: [Number, String]
    },
    /**
     * Kích thước đường viền
     */
    borderSize: {
      default: "1px",
      type: String
    },
    /**
     * Tiêu đề của control
     */
    title: {
      type: String
    },
    /**
     * Diễn giải thêm cho control
     */
    tooltip: {
      type: String
    },
    /**
     * Title dành riêng cho việc xử lý validation trên grid (do không muốn show title lên)
     */
    titleValidation: {
      type: String
    },
    /**
     * Trường bắt buộc nhập
     * Mặc định false
     */
    required: {
      type: Boolean,
      default: false
    },
    /**
     * Kí hiệu cảnh báo trường bắt buộc nhập không được bỏ trống
     */
    textRequired: {
      type: String,
      default: "*",
      required: false
    },
    /**
     * Có hiển thị ký tự required ra không
     */
    requiredVisible: {
      type: Boolean,
      default: false
    },
    /**
     * Các rule phục vụ cho validate control
     */
    rules: {
      type: [Object, String],
      default: ""
    },
    /**
     * id của validator provider
     */
    vid: {
      type: String
    },
    /**
     * Cho phép hiển thị số 0 ở đầu
     * [`allow`, `deny`, `keep`]
     * Mặc định là allow: Cho phép nhập nhưng sẽ mất khi focus ra bên ngoài
     */
    leadingZero: {
      type: String,
      default: "allow"
    },
    /**
     * Cờ bật tắt icon tăng giảm giá trị của number
     */
    isSpin: {
      type: [Boolean, String],
      default: false
    },
    /**
     * Format giá trị theo tùy chọn của hệ thống
     */
    formatType: {
      type: [Number, String],
      default: 0
    },
    /**
     * Cho phép hiển thị đầy đủ số lượng phần thập phân
     */
    allowDecimalPadding: {
      type: Boolean,
      default: false
    },
    /**
     * Cho phép phân tách các kí tự phần nguyên với nhau
     */
    allowDigitGroupSeparator: {
      type: [Boolean, String],
      default: true
    },
    /**
     * Set giá trị default của number khi người dùng không set giá trị
     */
    autoZero: {
      type: Boolean,
      default: false
    },
    /**
     * Cho phép có giá trị là null/undefined mà không phải set mặc định là 0
     * @author NVLAM 28.02.2020
     */
    isNullable: {
      type: Boolean,
      default: false
    },
    readOnly: {
      default: false,
      type: Boolean
    },
    decimalPlaces: {
      default: 0,
      type: Number
    },
    tabindex: {
      type: [String, Number],
      default: 1
    },
    disabled: {
      type: Boolean,
      default: false
    },
    paramValidate: {
      type: Object,
      default: function() {
        return { isValid: false, message: "", rules: "custom" };
      }
    },
    editable: {
      type: Boolean,
      default: false
    },
    permission: {
      type: Boolean,
      default: true
    },
    // bắt buộc dùng decimal tự gán thay vì lấy từ thiết lập
    forceDecimalPlaces: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      focused: false,
      internalText: this.value,
      // set chế độ edit = fasle khi editable = true
      editingMode: !this.editable,
      oldValue: null,
      controlRef: MsControlRef.MsNumber,
    };
  },

  computed: {
    listeners() {
      return {
        ...this.$listeners,
        input: evt => {
          this.$emit("inputed", this.getValue());
          this.onInput();
        },
        change: evt => {
          this.$emit("change", this.getValue(), evt);
        },
        blur: event => {
          this.onBlur();
          this.focused = false;
          this.$emit("blurEvent", this.getValue(), event);
          this.$emit("blur", this.getValue());
        },
        keydown: event => this.$emit("keydown", event),
        focus: event => {
          this.selectOnClick = true;
          // pvduong1 - 06/04/2020 - Khi focus vào thì select all dữ liệu trong controll
          event.target.select();
          this.focused = true;
          this.$emit("focus", event);
          this.changeFocus(true);
        },
        keyup: event => {
          this.$emit("keyup", this.getValue());
        },
        "autoNumeric:rawValueModified": this.updateVModel
      };
    },
    styleInput() {
      return {
        width: !isNaN(this.width)
          ? this.width + "px !important"
          : this.width + " !important",
        height: !isNaN(this.height)
          ? this.height + "px !important"
          : this.height + " !important"
      };
    },
    boderInput() {
      return {
        border: `${this.borderSize} solid rgb(186, 190, 197)`
      };
    },

    /**
     * @returns {Object}
     */
    anInfo() {
      return {
        value: this.value,
        numberFormat: this.$ms.session
      };
    },
    getInternalText() {
      return this.internalText
        ? formatMoney(this.internalText)
        : (this.autoZero ? 0: "");
    },
    isEditingMode() {
      return this.$store.getters.getEditingMode;
    }
  },

  watch: {
    /**
     * Sửa lại watch theo value theo hướng dẫn của anh bnduc
     * @author pcminh 13.05.2020
     */
    value(newValue, oldValue) {
      const me = this;
      if (
        // Make sure this is only called when the value is set by an external script, and not from a user input
        me.getValue() !== newValue &&
        // Compare the 'newValue' with the current 'oldValue' so we do not `set` it again if it's not needed
        newValue !== oldValue
      ) {
        // The modification comes from a script, so we need to reformat the new value `newValue.value`

        me.autoNumeric.set(newValue ? newValue : "");
      }
      me.internalText = me.getValue();
      this.validate(this.internalText);
      me.$emit("changeValue", newValue, oldValue, me);
    },
    /**
     * custom validate
     */
    paramValidate(val) {
      let me = this;
      //Nếu validate đang true và kphai custom thì return
      if (
        me.errorProvider &&
        me.errorProvider["isValid"] &&
        (!val || (val && me.errorProvider["rules"] != val.rules))
      ) {
        return;
      }

      if (val.isValid) {
        me.$set(me.errorProvider, "message", val.message);
        me.$set(me.errorProvider, "isValid", true);
        me.errorProvider.rules = val.rules;
        me.errorProvider.element = me.$refs[this.controlRef];
      } else {
        me.errorProvider = {};
      }
    },
    editable:{
      immediate: true,
      handler(val) {
        this.$store.dispatch("setEditingMode", false);
        this.editingMode = !val;
      }
    }
  },

  mounted() {
    // Initialize the autoNumeric element

    let me = this,
      options = me.initOptions();
    if (me.autoZero) {
      options.emptyInputBehavior = "zero";
    }
    //bỏ đi để control trên grid khi alt+tab xem số liệu ở chỗ khác quay lại thì bị select all text
    options.selectOnFocus = false;
    me.autoNumeric = new AutoNumeric(me.$refs[this.controlRef], options);

    if (this.value !== null && this.value !== "") {
      this.autoNumeric.set(this.value);
      // The `v-model` must be updated with that default value on startup
      this.updateVModel();
    }

    if (this.autofocus) {
      this.$nextTick(() => this.$refs[this.controlRef].focus());
    }
  },

  /**
   * Chuyển một số biến không cần reactive vào đây để tối ưu
   */
  created() {
    const me = this;
    /**
     * Cờ xác định có phải người dùng đang thực hiện typing trên control hay không
     */
    me.typing = false;
    me.valid = false;
    me.autoNumeric = null;
  },
  methods: {
    onDoubleClick() {
      this.permission && this.clickEditBtn();
    },
    // Sự kiện blur
    blurHandler(valid, errors) {
      let me = this;
      if (!valid) {
        this.$emit("validationError", errors);
      }
      this.changeFocus(false);
    },
    /**
     * Thay đổi trạng thái focus
     */
    changeFocus(booleanx) {
      this.focused = booleanx;
    },
    /**
     * Thực hiện focus vào ô input number
     */
    focusInput() {
      this.$refs[this.controlRef].focus();
    },
    /**
     * Sau sẽ bổ sung thêm DataType (Số tiền, Số lượng, tỷ lệ,...) để xác định đúng định dạng
     * Việc khởi tạo này sau này sẽ để 1 object mặc định trên session để tối ưu
     */
    initOptions() {
      let me = this;
      let options = {
        allowDecimalPadding: me.allowDecimalPadding, //Cho phép hiển thị đầy đủ số lượng phần thập phân
        decimalCharacter: ",", //Kí tự phân tách phần nguyên với phần thập phân
        digitGroupSeparator: ".", //Kí tự phân tách phần nguyên với nhau
        decimalCharacterAlternative: ".",
        negativePositiveSignPlacement: "p", //set vị trí dấu âm: [p - prefix, s - suffix, r - right, l - left]
        // emptyInputBehavior : 'zero', //set giá trị hiển thị mặc định khi number ko có giá trị là 0
        modifyValueOnWheel: "false", //hỗ trợ có lăn chuột thay đổi giá trị hay không
        // currencySymbol: sessionObj.CurrencySymbol,
        currencySymbolPlacement: "s", //vị trị đặt dấu tiền tệ
        // roundingMethod: 'U',
        maximumValue: me.max,
        minimumValue: me.min,
        leadingZero: me.leadingZero //cho phép giữ số 0 ở đầu
      };
      options.decimalPlaces = me.decimalPlaces;

      // Lấy dữ liệu thiết lập định dạng số
      let numberFormatConfig = me.$store.getters.getNumberFormatConfig;
      if(numberFormatConfig && !this.forceDecimalPlaces){
        options.digitGroupSeparator = numberFormatConfig.thousandsSeparatorCharacter;
        options.decimalCharacter = numberFormatConfig.decimalSeparatorCharacter;
        options.decimalPlaces = numberFormatConfig.numberOfDecimal;
      }
      if (me.allowDigitGroupSeparator === false) {
        options.digitGroupSeparator = "";
      }
      return options;
    },

    /**
     * Update the v-model value and make the parent aware of that change.
     *
     * @param {Event} event This is needed if we want to use the `event.timeStamp` attribute
     */
    updateVModel(event) {
      let me = this;
      if (me.autoNumeric != null) {
        let value = me.getValue();
        me.$emit("input", value, event);
        // me.$emit('change', value, event);
        // me.validate(this.internalText = value);
      }
    },

    upDownValue(e) {
      let me = this;
      if (me.isSpin) {
        if (event.keyCode === 38) {
          this.plus();
        }
        if (event.keyCode === 40) {
          this.less();
        }
      } else {
        if (me.isGridEditor) {
          let el = me.$refs[this.controlRef];
          if (
            el.selectionStart == 0 &&
            el.selectionEnd == el.value.length &&
            (e.which == 39 || e.which == 37)
          ) {
            let readonly = el.readOnly;
            if (!el.readOnly) {
              el.readOnly = true;
            }
            setTimeout(function() {
              el.readOnly = readonly;
            });
          }
        }
      }
    },
    plus() {
      this.changeFocus(true);
      let me = this;
      let value = me.value;
      //nvhieu 17/12/2020 thêm điều kiện check disable trontrol
      if (!value && !me.disabled) {
        value = 0;
      }
      value = parseInt(value);
      if (value < me.max && !me.disabled) {
        //nttung3 03.08.2020: sửa để cộng đúng giá trị step truyền vào
        value += parseInt(me.step);
        me.autoNumeric.set(value);
        me.updateVModel();
        this.$emit("change", me.getValue(), event);
        this.validate((this.internalText = this.getValue()));
      }
    },
    less() {
      this.changeFocus(true);
      let me = this;
      let value = me.value;
      if (!value && !me.disabled) {
        value = 0;
      }
      value = parseInt(value);
      //nvhieu 17/12/2020 thêm điều kiện check disable control
      if (value > me.min && !me.disabled) {
        //nttung3 03.08.2020: sửa để trừ đúng giá trị step truyền vào
        value -= parseInt(me.step);
        // if (value < 10) {
        // 	value = '0' + value;
        // }
        me.autoNumeric.set(value);
        me.updateVModel();
        this.$emit("change", me.getValue(), event);
        this.validate((this.internalText = this.getValue()));
      }
    },
    /**
     * Lấy giá trị của control
     */
    getValue() {
      const me = this;
      if (me.autoNumeric.rawValue) {
        return me.autoNumeric.rawValue.length < 16 ? me.autoNumeric.getNumber() : me.autoNumeric.rawValue;
      }
      return null;
    },
    /**
     * Gán giá trị vào control
     */
    setInternalValue(value) {
      let me = this;
      me.autoNumeric.set(value);
      me.updateVModel();
    },
    /**
     * Sự kiện người dùng gõ vào ô input (dùng để bật cờ typing)
     */
    onInput() {
      this.typing = true;
    },

    onBlur() {
      this.typing = false;
    },
    setValue(value) {
      const me = this;

      if (me.$refs[this.controlRef]) {
        me.$refs[this.controlRef].value = null;
      }

      if (me.autoNumeric && typeof value !== "undefined" && value != null) {
        me.setInternalValue(value);
      } else {
        me.innerValue = value;
        me.value = value;
      }
    },
    onInputClick() {},
    onInputFocus() {},
    /**
     * Hàm xử lý khi click icon edit
     * created by pdthien 21.01.2021
     */
    clickEditBtn() {
      if (!this.isEditingMode) {
      this.oldValue = this.value;
      this.editingMode = !this.editingMode;
      this.$nextTick(() => this.$refs[this.controlRef] && this.$refs[this.controlRef].focus());
      this.$emit("clickEditBtn");
      this.$store.dispatch("setEditingMode", true);
      }
    },
    /**
     * Hàm xử lý khi đóng chế độ edit
     * created by pdthien 21.01.2021
     */
    closeEditMode() {
      this.editingMode = false;
      this.value = this.oldValue;
      this.$emit("closeEditMode");
      this.$store.dispatch("setEditingMode", false);
    },
    /**
     * Hàm xử lý khi lưu edit
     * created by pdthien 21.01.2021
     */
    saveEditMode() {
      if (!this.errorProvider.isValid) {
        this.$emit("onSaveData");
      }
    },
    /**
     * Hàm set editingMode = false
     */
    closeEditingMode() {
      this.editingMode = false;
      this.$store.dispatch("setEditingMode", false);
    }
  }
};
</script>
<style lang="scss" scoped>
@import "@/assets/scss/controls/msNumber.scss";
.input-readOnly {
  background: #f3f3f3 !important;
  pointer-events: none;
}
.bg-white {
  background: #fff;
}
.h-15 {
  height: 15px;
}
.text-border {
  p {
    display: flex;
    flex-direction: column-reverse;
    padding-left: 8px;
  }
  .mi-lock {
    opacity: 0;
  }
  &:hover {
    .mi-lock {
      opacity: 1;
    }
  }
}
</style>
